import React, { useState, useEffect } from 'react';
import MainMenu from './components/MainMenu';
import DailyLog from './components/DailyLog';
import Statistics from './components/Statistics';
import AdminDashboard from './components/AdminDashboard';
import Login from './components/Login';
import OpportunityChecker from './components/OpportunityChecker';
import ThemeToggle from './components/ThemeToggle';
import CompCalculator from './components/CompCalculator';
import Mileage from './components/Mileage';
import ManagerAppointments from './components/ManagerAppointments';
import DirectorAppointments from './components/DirectorAppointments';
import TeamPoints from './components/TeamPoints';
import DailyActivity from './components/DailyActivity';
import Motis from './components/Motis';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Sessions older than this predate login tokens (userId was the raw user id,
// which the server no longer accepts), so they are sent back to the login screen.
const MIN_SESSION_VERSION = 3;

function App() {
  const [currentView, setCurrentView] = useState('menu');
  const [user, setUser] = useState(null);
  const [originalUser, setOriginalUser] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('originalSession'));
      return s && s.session_version >= MIN_SESSION_VERSION ? s : null;
    } catch { return null; }
  });

  useEffect(() => {
    // Check for existing session — must be from the login-token era
    const userSession = localStorage.getItem('userSession');
    if (userSession) {
      try {
        const session = JSON.parse(userSession);
        if (!session.session_version || session.session_version < MIN_SESSION_VERSION || !session.id) {
          localStorage.removeItem('userSession');
          localStorage.removeItem('originalSession');
          return;
        }
        setUser(session);
      } catch (err) {
        console.error('Invalid session data');
        localStorage.removeItem('userSession');
      }
    }
  }, []);

  // If the server ever says the session is no longer valid (logged out
  // elsewhere, PIN reset, expired), drop back to the login screen instead of
  // leaving every screen showing errors. The login call itself is excluded —
  // a wrong PIN also returns 401.
  useEffect(() => {
    const realFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await realFetch(...args);
      const url = String(args[0]?.url || args[0]);
      if (res.status === 401 && url.startsWith(API_URL) && !url.includes('/api/login/')
          && localStorage.getItem('userSession')) {
        localStorage.removeItem('userSession');
        localStorage.removeItem('originalSession');
        setUser(null);
        setOriginalUser(null);
        setCurrentView('menu');
      }
      return res;
    };
    return () => { window.fetch = realFetch; };
  }, []);

  const handleLogin = (data) => {
    const userData = {
      // userId is the login token sent as X-User-ID; id is the real user id
      // for "is this me?" checks.
      userId: data.session_token,
      id: data.user.id,
      username: data.user.username,
      is_admin: data.is_admin || data.user.is_superuser,
      is_director: data.is_director || false,
      is_senior_director: data.is_senior_director || false,
      manages_people: data.manages_people || false,
      is_rep: data.is_rep != null ? data.is_rep : true,
      is_trainer: data.is_trainer || false,
      profile: data.user.profile,
      session_version: data.session_version || MIN_SESSION_VERSION,
    };
    setUser(userData);

    // Update localStorage with complete user data
    localStorage.setItem('userSession', JSON.stringify(userData));
  };

  const handleUserUpdate = (updatedUserData) => {
    // Update the user state with new profile data
    const updatedUser = {
      ...user,
      profile: updatedUserData.profile,
      // Keep other existing user data
    };
    setUser(updatedUser);

    // Update localStorage
    localStorage.setItem('userSession', JSON.stringify(updatedUser));
  };

  const handleImpersonate = (data) => {
    setOriginalUser(user);
    localStorage.setItem('originalSession', JSON.stringify(user));
    handleLogin(data);
    setCurrentView('menu');
  };

  const handleReturnToSelf = () => {
    if (!originalUser) return;
    setUser(originalUser);
    localStorage.setItem('userSession', JSON.stringify(originalUser));
    localStorage.removeItem('originalSession');
    setOriginalUser(null);
    setCurrentView('menu');
  };

  const handleLogout = () => {
    // End the sessions on the server too (the impersonated one and, if "Login
    // as" was active, the real user's). Fire-and-forget: logout must not hang.
    [user, originalUser].filter(Boolean).forEach((s) => {
      fetch(`${API_URL}/api/logout/`, {
        method: 'POST', headers: { 'X-User-ID': String(s.userId) },
      }).catch(() => {});
    });
    localStorage.removeItem('userSession');
    localStorage.removeItem('originalSession');
    setUser(null);
    setOriginalUser(null);
    setCurrentView('menu');
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
  };

  if (!user) {
    return (
      <>
        <ThemeToggle />
        <Login onLogin={handleLogin} />
      </>
    );
  }

  const renderView = () => {
    switch (currentView) {
      case 'log':
        return <DailyLog onBack={() => handleViewChange('menu')} user={user} />;
      case 'statistics':
        return <Statistics onBack={() => handleViewChange('menu')} user={user} />;
      case 'admin':
        return <AdminDashboard onBack={() => handleViewChange('menu')} user={user} onImpersonate={handleImpersonate} />;
      case 'opp_checker':
        return <OpportunityChecker onBack={() => handleViewChange('menu')} />;
      case 'comp_calculator':
        return <CompCalculator onBack={() => handleViewChange('menu')} user={user} />;
      case 'mileage':
        return <Mileage onBack={() => handleViewChange('menu')} user={user} />;
      case 'manager_appointments':
        return <ManagerAppointments onBack={() => handleViewChange('menu')} user={user} />;
      case 'director_appointments':
        return <DirectorAppointments onBack={() => handleViewChange('menu')} user={user} />;
      case 'team_points':
        return <TeamPoints onBack={() => handleViewChange('menu')} user={user} />;
      case 'daily_activity':
        return <DailyActivity onBack={() => handleViewChange('menu')} user={user} />;
      case 'motis':
        return <Motis onBack={() => handleViewChange('menu')} user={user} />;
      default:
        return (
          <MainMenu
            onViewChange={handleViewChange}
            user={user}
            onLogout={handleLogout}
            onUserUpdate={handleUserUpdate}  // Pass the update handler
          />
        );
    }
  };

  return (
    <div className="App">
      <ThemeToggle />
      {originalUser && (
        <div className="impersonate-banner">
          <span>Viewing as <strong>{user.username}</strong></span>
          <button onClick={handleReturnToSelf} className="impersonate-return-btn">
            ← Back to {originalUser.username}
          </button>
        </div>
      )}
      {renderView()}
    </div>
  );
}

export default App;
